import { useQuery } from '@tanstack/react-query';
import { endpoints } from '../api/endpoints';
import { useMasters } from '../hooks/useMasters';

interface Rate {
  id: string;
  metal_id: string;
  purity_id: string | null;
  rate_type: string;
  rate_per_gram: string | null;
  rate_per_fine_gram: string | null;
  effective_from: string;
  status: string;
}

/**
 * Today's rates, laid out to be read while judging a purchase.
 *
 * A jeweller buying stock or old gold is doing arithmetic in their head against
 * the rate, and leaving the screen to check it breaks the count. Both sides are
 * shown — the sale rate a new piece is stocked near, and the purchase rate old
 * gold is paid on — because a purchase screen touches both.
 */
export function RatePanel() {
  const masters = useMasters();
  const { data } = useQuery({
    queryKey: ['rates'],
    queryFn: () => endpoints.pricing.rates(),
    refetchInterval: 120_000,
    staleTime: 60_000,
  });

  const rates = (data?.data ?? []) as unknown as Rate[];
  const approved = rates.filter((r) => r.status === 'APPROVED');

  // The newest approved rate for each metal/purity/type combination.
  const latest = new Map<string, Rate>();
  for (const rate of approved) {
    const key = `${rate.metal_id}:${rate.purity_id ?? 'any'}:${rate.rate_type}`;
    const held = latest.get(key);
    if (!held || rate.effective_from > held.effective_from) latest.set(key, rate);
  }

  const forType = (type: string): { label: string; value: string }[] =>
    [...latest.values()]
      .filter((r) => r.rate_type === type)
      .map((r) => ({
        label:
          masters.purities.find((p) => p.id === r.purity_id)?.code ??
          masters.metals.find((m) => m.id === r.metal_id)?.code ??
          '',
        value: r.rate_per_gram ?? r.rate_per_fine_gram ?? '',
      }))
      .filter((r) => r.label && r.value);

  const sale = forType('SALE');
  const purchase = forType('PURCHASE');

  if (sale.length === 0 && purchase.length === 0) return null;

  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-md border border-silver-100 bg-surface px-4 py-2.5 shadow-card">
      {sale.length > 0 ? (
        <div className="flex items-baseline gap-3">
          <span className="text-2xs uppercase tracking-wide text-ink-soft">Sale rate</span>
          {sale.map((r) => (
            <span key={r.label} className="flex items-baseline gap-1">
              <span className="text-2xs text-ink-faint">{r.label}</span>
              <span className="num text-sm font-semibold">
                ₹{Number(r.value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </span>
            </span>
          ))}
        </div>
      ) : null}

      {purchase.length > 0 ? (
        <div className="flex items-baseline gap-3">
          <span className="text-2xs uppercase tracking-wide text-ink-soft">Buy rate</span>
          {purchase.map((r) => (
            <span key={r.label} className="flex items-baseline gap-1">
              <span className="text-2xs text-ink-faint">{r.label}</span>
              <span className="num text-sm font-semibold text-gold-700">
                ₹{Number(r.value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </span>
            </span>
          ))}
          <span className="text-2xs text-ink-faint">per fine g</span>
        </div>
      ) : null}
    </div>
  );
}
